/**
 * Calendar days as ISO `YYYY-MM-DD` strings (plan dates, shopping ranges). Pure arithmetic on the
 * date itself, so the result never depends on the device's time zone.
 */
const toUtc = (iso: string) => new Date(`${iso}T00:00:00Z`);
const fromUtc = (d: Date) => d.toISOString().slice(0, 10);

export function addDays(iso: string, days: number): string {
  const d = toUtc(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return fromUtc(d);
}

/** ISO weekday: 1 = Monday … 7 = Sunday. */
export function isoWeekday(iso: string): number {
  return toUtc(iso).getUTCDay() || 7;
}

/** The Monday of the week `iso` falls in. */
export const weekStart = (iso: string): string => addDays(iso, 1 - isoWeekday(iso));

/** The seven days of the week starting `monday`. */
export const weekDays = (monday: string): string[] =>
  Array.from({ length: 7 }, (_, i) => addDays(monday, i));

/** ISO 8601 week number (KW). */
export function isoWeek(iso: string): number {
  const thursday = toUtc(addDays(iso, 4 - isoWeekday(iso)));
  const jan1 = Date.UTC(thursday.getUTCFullYear(), 0, 1);
  return Math.floor((thursday.getTime() - jan1) / 86_400_000 / 7) + 1;
}

/** Whole days from `a` to `b` (negative when `b` is earlier). */
export const daysBetween = (a: string, b: string): number =>
  Math.round((toUtc(b).getTime() - toUtc(a).getTime()) / 86_400_000);

/** The calendar day of `instant` in `timeZone` (the household's, or the device's). */
export function localDate(instant: Date = new Date(), timeZone?: string): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}
